import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// How long after a navigation we keep pinning the page to the top while
// async content (e.g. API responses) loads in and changes the page height.
const PIN_DURATION_MS = 2000;

const scrollToTop = () => {
    window.scrollTo(0, 0);
    // Some mobile browsers scroll the documentElement/body rather than the window.
    if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
};

const ScrollToTop = () => {
    const { pathname } = useLocation();

    // Stop the browser from restoring the previous scroll position on navigation.
    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
    }, []);

    useEffect(() => {
        scrollToTop();
        const raf = requestAnimationFrame(scrollToTop);

        // Data-driven screens finish their API calls after this effect runs and
        // then grow in height. Keep resetting to the top while that happens, but
        // stop as soon as the user deliberately scrolls/interacts.
        let userHasInteracted = false;

        const stopPinning = () => {
            userHasInteracted = true;
        };

        const interactionEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
        interactionEvents.forEach((event) =>
            window.addEventListener(event, stopPinning, { passive: true })
        );

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                if (!userHasInteracted) {
                    scrollToTop();
                }
            });
            resizeObserver.observe(document.body);
        }

        const timeoutId = setTimeout(() => {
            resizeObserver?.disconnect();
            interactionEvents.forEach((event) =>
                window.removeEventListener(event, stopPinning)
            );
        }, PIN_DURATION_MS);

        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(timeoutId);
            resizeObserver?.disconnect();
            interactionEvents.forEach((event) =>
                window.removeEventListener(event, stopPinning)
            );
        };
    }, [pathname]);

    return null;
};

export default ScrollToTop;
